/**
 * Coerce the backend's `interests` into a real string[].
 *
 * `users.interests` is a TEXT column and the API returns it exactly as Postgres
 * serialized whatever it was given — most often the array literal
 * `{"education","healthcare"}`, sometimes a comma list, sometimes a JSON string.
 * Pro's profile form writes it as free text (its zod schema is `z.string()`),
 * so a plain sentence is a legitimate stored value too.
 *
 * The citizen UI treats interests as an array (chip toggles, priority chips,
 * chat topic suggestions), so normalize once here and let every consumer read
 * an array.
 *
 * Apply this in a react-query `select`, not in `queryFn`. The citizen app runs
 * under PersistQueryClientProvider: a rehydrated cache hands its stored value
 * straight to consumers without re-running `queryFn`, so coercion that lives in
 * the fetch is skipped exactly when the stored shape is the stale one. `select`
 * runs on every read, rehydrated or fetched.
 */
export function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value !== 'string') return [];

  const s = value.trim();
  if (!s) return [];

  // Postgres array literal: {"a","b"} or {a,b}
  if (s.startsWith('{') && s.endsWith('}')) {
    const inner = s.slice(1, -1).trim();
    if (!inner) return [];
    return inner
      .split(',')
      .map((x) => x.trim().replace(/^"(.*)"$/, '$1'))
      .filter(Boolean);
  }

  // JSON array string: ["a","b"]
  if (s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map((v) => String(v));
    } catch {
      // Not JSON after all — fall through to the comma split.
    }
  }

  return s.split(',').map((x) => x.trim()).filter(Boolean);
}
